"use client";

import React from 'react';
import { Badge } from "./badge";
import { CLIENT_TYPES, CLIENT_STATUSES, type ClientType, type ClientStatus } from "@/types/customer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { CLIENT_TYPE_COLORS } from "@/types/customer";
import { cn } from "@/lib/utils";

interface ClientBadgeProps {
  type: "type" | "status";
  value: string;
  editable?: boolean;
  onValueChange?: (value: ClientStatus) => void;
  className?: string;
}

const badgeVariants = {
  secondary: "bg-secondary text-secondary-foreground hover:bg-secondary-foreground hover:text-secondary",
  destructive: "bg-destructive text-destructive-foreground hover:bg-destructive-foreground hover:text-destructive",
  outline: "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground",
  default: "bg-primary text-primary-foreground hover:bg-primary-foreground hover:text-primary",
  type: {
    'emerald': 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-800',
    'blue': 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800',
    'purple': 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-400 dark:border-purple-800',
    'yellow': 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-400 dark:border-yellow-800',
    'pink': 'bg-pink-100 text-pink-700 border-pink-200 dark:bg-pink-950 dark:text-pink-400 dark:border-pink-800',
    'orange': 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-400 dark:border-orange-800',
    'red': 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-400 dark:border-red-800',
    'indigo': 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-950 dark:text-indigo-400 dark:border-indigo-800',
    'slate': 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-950 dark:text-slate-400 dark:border-slate-800',
  }
};

export function ClientBadge({ type, value, editable = false, onValueChange, className }: ClientBadgeProps) {
  const options = type === "type" ? CLIENT_TYPES : CLIENT_STATUSES;
  const variant = type === "type" ? "outline" : "secondary";

  const getTypeColor = (clientType: string) => {
    return CLIENT_TYPE_COLORS[clientType as keyof typeof CLIENT_TYPE_COLORS] || 'slate';
  };

  if (editable && onValueChange) {
    return (
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className={cn("h-7 px-2", className)}>
          <SelectValue placeholder={`Select ${type}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div className={cn(
      "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
      type === 'type' && badgeVariants.type[getTypeColor(value)],
      className
    )}>
      {value}
    </div>
  );
} 